import { APP_NAME, JOIN_BUTTON, OPERATOR, REQUEST_BUTTON, SUPPORT_CONTACT } from '@eventping/shared'
import { Link } from '@tanstack/react-router'
import type * as React from 'react'
import { Card, Footer, Page, Wordmark } from '@/components/shell'
import { useMe } from '@/lib/api'

/**
 * The privacy policy and terms that toll-free verification asks for (docs/MESSAGING-COMPLIANCE.md).
 * Served at /privacy and /terms. Plain, short, and true to what the app does.
 */

/** Who “we” are, near the top of both pages: the operator named in the toll-free registration. */
function Operator({ support, doc }: { support: string; doc: string }) {
  return (
    <p data-testid="operator">
      {APP_NAME} is operated by {OPERATOR}. In {doc}, “we” and “us” mean {OPERATOR}. Contact:{' '}
      {support.includes('@') ? (
        <a href={`mailto:${support}`} className="underline">
          {support}
        </a>
      ) : (
        support
      )}
      .
    </p>
  )
}

function Legal({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Page
      header={
        <Link to="/">
          <Wordmark />
        </Link>
      }
    >
      <Card className="space-y-4 [&_h2]:mt-6 [&_h2]:text-lg [&_h2]:font-semibold [&_li]:ml-5 [&_li]:list-disc">
        <h1 className="text-2xl font-semibold">{title}</h1>
        {children}
      </Card>
      <Footer />
    </Page>
  )
}

const UPDATED = 'October 24, 2026'
/** The terms changed again for text credit (CREDIT_TERMS_VERSION). */
const TERMS_UPDATED = 'October 25, 2026'

export function PrivacyPage() {
  const support = useMe().data?.support ?? SUPPORT_CONTACT
  return (
    <Legal title={`${APP_NAME} privacy policy`}>
      <p className="text-sm text-muted-foreground">Last updated {UPDATED}</p>
      <Operator support={support} doc="this policy" />
      <p>
        {APP_NAME} sends step-by-step text updates about an event to people who signed up for them
        on that event’s join page. We collect as little as we can, and we never sell it.
      </p>
      <h2>What we collect</h2>
      <ul>
        <li>
          Organizers: the name and email address from sign-in, and the events, steps and people they
          add.
        </li>
        <li>
          People who sign up for an event: the name, email address and/or mobile number you enter on
          the join page, which updates you chose, and messages you send to the event.
        </li>
        <li>
          Email addresses are used only to send the updates you asked for (a confirmation, then one
          email per step) and, for a private event, an optional note if a request is turned down.
          Every email has a one-click unsubscribe link.
        </li>
        <li>
          A record of your consent when you sign up: the time, the event, your number, the exact
          consent text you agreed to, a one-way hash of your device’s IP address (not the address
          itself), and your browser’s user agent. We keep it to show that you asked for the texts.
        </li>
        <li>
          If an organizer adds you by name and number or email before you sign up, we store those to
          show them who hasn’t joined yet. We don’t message you unless you sign up yourself.
        </li>
        <li>
          A log of the texts we send and receive, to deliver them, honor opt-outs, and keep costs in
          check.
        </li>
      </ul>
      <h2>What we don’t do</h2>
      <ul>
        <li>
          <strong>
            Mobile numbers and opt-in consent are not shared with or sold to third parties or
            affiliates for marketing purposes.
          </strong>{' '}
          No mobile information is shared with third parties or affiliates for marketing or
          promotional purposes, and text messaging originator opt-in data and consent are never
          shared with any third parties.
        </li>
        <li>
          Email addresses are never shared with or sold to anyone, and never used for marketing.
        </li>
        <li>No analytics, ads or tracking on any page.</li>
        <li>Other people on an event never see your number, and see only your first name.</li>
      </ul>
      <h2>Who processes it</h2>
      <p>
        Our SMS provider (Twilio) delivers texts and receives replies. Our hosting provider
        (Railway) stores the database. They process data only to provide their service to us, and
        may not use it for their own purposes.
      </p>
      <h2>Your choices</h2>
      <ul>
        <li>Reply STOP to any text to stop all texts from {APP_NAME}. Reply START to resume.</li>
        <li>Use the unsubscribe link in any email to stop emails about that event.</li>
        <li>Use “Leave this event” or “Never add me to events” on any event page.</li>
        <li>Ask us to delete your data: {support}.</li>
      </ul>
      <h2>Retention</h2>
      <p>
        Event page links expire after two weeks. When an organizer or a person on an event asks, we
        delete that event’s contacts, messages and text log. We keep opt-out and consent records so
        we can keep honoring them and show how each person opted in.
      </p>
      <h2>Contact</h2>
      <p>Questions about privacy: {support}.</p>
    </Legal>
  )
}

export function TermsPage() {
  const me = useMe().data
  const support = me?.support ?? SUPPORT_CONTACT
  // Texts-off mode keeps the credit terms (the toll-free registration points at them), marked.
  const textsOff = me?.textsEnabled === false
  return (
    <Legal title={`${APP_NAME} terms of service`}>
      <p className="text-sm text-muted-foreground">Last updated {TERMS_UPDATED}</p>
      <Operator support={support} doc="these terms" />
      <h2>The service</h2>
      <p>
        {APP_NAME} lets an organizer send short text updates about the steps of an event to people
        who have signed up to receive them, and shows those people a live checklist and a shared
        conversation.
      </p>
      <h2>Text messaging program</h2>
      <ul>
        <li>Program name: {APP_NAME} event updates.</li>
        <li>
          How you opt in: on an event’s join page you enter your own mobile number and tick an
          unchecked box agreeing to receive text message updates about that event from {APP_NAME},
          then tap “{JOIN_BUTTON}” (or “{REQUEST_BUTTON}” on a private event, where nothing is sent
          until the organizer approves you). We send one confirmation text. Organizers cannot sign
          you up, and adding your number to an event never sends you a text.{' '}
          <Link to="/opt-in" className="underline">
            How opt-in works
          </Link>
          .
        </li>
        <li>
          What you get: a text when the organizer marks a step of that event done, each with a link
          to the event’s updates page. No marketing.
        </li>
        <li>Message frequency varies with the event, typically a few messages per event.</li>
        <li>Msg & data rates may apply.</li>
        <li>
          Reply STOP to cancel at any time; you’ll get one confirmation and no further texts. Reply
          START to resume.
        </li>
        <li>Reply HELP for help, or contact {support}.</li>
        <li>Carriers are not liable for delayed or undelivered messages.</li>
      </ul>
      <h2>Email updates</h2>
      <ul>
        <li>
          If you give an email address on a join page, you get a confirmation and then one email per
          step of that event. No marketing.
        </li>
        <li>Every email has a one-click unsubscribe link.</li>
      </ul>
      <h2>Organizers</h2>
      <ul>
        <li>
          Only share your join link with people who know you and want to hear about the event.
        </li>
        <li>No marketing, sales, or messages on someone else’s behalf.</li>
        <li>Free accounts have limits on events, people, steps and texts, which we may change.</li>
      </ul>
      <h2 id="text-credit" className="scroll-mt-24">
        Text credit
      </h2>
      {textsOff && (
        <p data-testid="texts-not-available">
          <strong>Text updates are not yet available.</strong>
        </p>
      )}
      <p>
        <strong>Text credit.</strong> Texts are paid for in advance with text credit, sold in packs.
        One text uses one credit. Email updates are free.{' '}
        <Link to="/pricing" className="underline">
          Prices
        </Link>
        .
      </p>
      <p>
        <strong>No refunds.</strong> All purchases of text credit are final. We don’t refund
        purchased packs or the free welcome credit, in whole or in part, including unused credit.
        Credit has no cash value.
      </p>
      <p>
        <strong>No expiry.</strong> Purchased credit doesn’t expire while your account is open.
      </p>
      <p>
        <strong>Failed texts.</strong> If a text is rejected by our provider or definitely fails to
        deliver, we add the credit back to your account automatically. A text with no delivery
        report is treated as sent.
      </p>
      <p>
        <strong>No transfers.</strong> Credit belongs to the account that bought or received it and
        can’t be moved, sold or combined with another account.
      </p>
      <p>
        <strong>Disputes.</strong> If you dispute a charge with your card issuer and the dispute is
        decided in your favor, we remove the credit from that purchase, which may pause texting
        until you buy more. Please contact us first; we’re glad to help.
      </p>
      <p>
        <strong>Sales tax.</strong> Prices exclude sales tax. Where we must collect it (California,
        from January 1, 2027), California sales tax is added at checkout for buyers in California,
        on its own line, before you pay.
      </p>
      <h2>No warranty</h2>
      <p>
        {APP_NAME} is provided as is. Texts can be delayed or fail; don’t rely on it for
        emergencies.
      </p>
      <h2>Contact</h2>
      <p>{support}</p>
    </Legal>
  )
}
